import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React from 'react';
import {Badge, Stack, styled} from '@mui/material';
import {Avatar, Body} from '@lightricks/react-design-system';
import formatCentsToDollars from '@/utils/formatCentsToDollars';
import {formattedDate, formattedUtcDate} from '@/utils/formattedDate';
import getEngagementRateString from '@/utils/getEngagementRateString';
import getFollowersCountString from '@/utils/getFollowersCountString';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import AssetsCell from '@/views/campaigns/creators-tab/components/assets-cell';
import {CREATOR_STATUS} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {
  CampaignCreatorRow,
  CreatorStatus,
  CampaignDeliverables,
  PageSelectionState,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import Checkbox from '@/components/checkbox';
import CampaignHistoryCell from '../../components/campaign-history-cell';
import CreatorRowMenu from '../../components/creator-row-menu';
import DeliverableCodeCell from '../../components/deliverable-code-cell';
import FulfillmentStatusCell from '../../components/fulfillment-status-cell';
import ListTagsCell from '../../components/list-tags-cell';
import SafeCollabBadge from '../../components/safe-collab-badge';
import {createFulfillmentActionSortOptions} from '../../utils/getFulfillmentActionPriority';

const TruncatedBody = styled(Body)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const COLUMN_ID = {
  Select: 'select',
  Actions: 'actions',
  CreatorName: 'creatorName',
  Followers: 'followers',
  EngRate: 'engagementRate',
  AvgViews: 'averageViews',
  CreatorLocation: 'location',
  AudienceLocation: 'audienceTopCountry',
  Lists: 'lists',
  SafeCollab: 'safeCollabStatus',
  Offer: 'costInCents',
  History: 'campaignHistoryCount',
  Applied: 'appliedDate',
  Notes: 'brandNote',
  Assets: 'assets',
  ShippingAddress: 'shippingAddressFormatted',
  ShippingStatus: 'shippingStatus',
  TrackingCode: 'trackingCode',
  GiftCode: 'giftCode',
  FulfillmentStatus: 'fulfillmentStatus',
  ContentDue: 'contentDue',
} as const;
interface UseCreatorsTabColumnsOptions {
  activeStatus: CreatorStatus;
  onActionComplete: () => void;
  hasFulfillment?: boolean;
  deliverablesType?: CampaignDeliverables | null;
  selectedCreatorIds?: Set<string>;
  pageSelectionState?: PageSelectionState;
  onSelectionChange?: (creatorId: string, isSelected: boolean) => void;
  onSelectAllPage?: (isSelected: boolean) => void;
}

// Flex column sizing — mirrors the global Creator CRM table behavior:
// columns share remaining width (grow: 1) while never shrinking below
// minSize or expanding past maxSize.
const DEFAULT_MIN_COLUMN_SIZE = 100;
const DEFAULT_MAX_COLUMN_SIZE = 292;
const SAFE_COLLAB_MIN_COLUMN_SIZE = 224;

function getFlexColumnSizing(minSize: number = DEFAULT_MIN_COLUMN_SIZE) {
  return {
    size: minSize,
    minSize,
    maxSize: DEFAULT_MAX_COLUMN_SIZE,
    grow: 1,
  } as const;
}

function useCreatorsTabColumns({
  activeStatus,
  onActionComplete,
  hasFulfillment = false,
  deliverablesType = null,
  selectedCreatorIds,
  pageSelectionState = 'none',
  onSelectionChange,
  onSelectAllPage,
}: UseCreatorsTabColumnsOptions): MRT_ColumnDef<CampaignCreatorRow>[] {
  const selectColumn: MRT_ColumnDef<CampaignCreatorRow>[] = onSelectionChange
    ? [
        {
          id: COLUMN_ID.Select,
          header: '',
          size: 24,
          minSize: 24,
          maxSize: 24,
          grow: false,
          enableSorting: false,
          Header: () => (
            <Checkbox
              testID="select-all-checkbox"
              checked={pageSelectionState === 'all'}
              indeterminate={pageSelectionState === 'partial'}
              onChange={(checked: boolean) => onSelectAllPage?.(checked)}
            />
          ),
          Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => {
            const isSelected =
              selectedCreatorIds?.has(row.original.id) ?? false;
            return (
              <Checkbox
                testID={`select-creator-${row.original.id}`}
                checked={isSelected}
                onChange={(checked: boolean) =>
                  onSelectionChange(row.original.id, checked)
                }
              />
            );
          },
        } as MRT_ColumnDef<CampaignCreatorRow>,
      ]
    : [];

  const actionsColumn: MRT_ColumnDef<CampaignCreatorRow> = {
    id: COLUMN_ID.Actions,
    header: '',
    size: 24,
    minSize: 24,
    maxSize: 24,
    grow: false,
    enableSorting: false,
    Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
      <CreatorRowMenu
        creator={row.original}
        onActionComplete={onActionComplete}
      />
    ),
  };

  const creatorNameColumn: MRT_ColumnDef<CampaignCreatorRow> = {
    id: COLUMN_ID.CreatorName,
    header: translate('Creator Name'),
    accessorKey: 'creatorName',
    enableSorting: true,
    ...getFlexColumnSizing(),
    Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => {
      const unreadCount = row.original.merchantCampaignUnreadMessagesCount;
      return (
        <Stack
          direction="row"
          alignItems="center"
          gap="12px"
          // minWidth: 0 lets the name shrink inside its flex parent;
          // overflow stays visible so the unread-count badge can extend
          // past the avatar edge.
          sx={{minWidth: 0, overflow: 'visible', width: '100%'}}
        >
          <Badge
            variant="standard"
            color="info"
            badgeContent={unreadCount}
            invisible={unreadCount === 0}
            overlap="circular"
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            sx={{flexShrink: 0}}
          >
            <Avatar
              size="sm"
              src={row.original.avatarUrl ?? undefined}
              type={row.original.avatarUrl ? 'picture' : 'initials'}
              initials={row.original.creatorName.charAt(0)}
            />
          </Badge>
          <Body
            size="md"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.creatorName}
          </Body>
        </Stack>
      );
    },
  };

  const safeCollabColumn: MRT_ColumnDef<CampaignCreatorRow> = {
    id: COLUMN_ID.SafeCollab,
    header: translate('Safe Collab Status'),
    accessorKey: 'safeCollabStatus',
    enableSorting: false,
    ...getFlexColumnSizing(SAFE_COLLAB_MIN_COLUMN_SIZE),
    Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
      <SafeCollabBadge status={row.original.safeCollabStatus} />
    ),
  };

  const listsColumn: MRT_ColumnDef<CampaignCreatorRow> = {
    id: COLUMN_ID.Lists,
    header: translate('Lists'),
    accessorKey: 'lists',
    enableSorting: false,
    ...getFlexColumnSizing(),
    Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
      <ListTagsCell
        lists={row.original.lists}
        isShortlisted={row.original.isShortlisted}
      />
    ),
  };

  const commonPrefix = [...selectColumn, actionsColumn, creatorNameColumn];

  if (activeStatus === CREATOR_STATUS.Hired) {
    const hiredSuffix = [
      {
        id: COLUMN_ID.ContentDue,
        header: translate('Content due'),
        accessorKey: 'contentDue',
        enableSorting: true,
        ...getFlexColumnSizing(),
        Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
          <Body size="md">
            {row.original.contentDue
              ? formattedUtcDate(
                  row.original.contentDue,
                  MONTH_SHORT_DAY_YEAR_FORMAT
                )
              : '-'}
          </Body>
        ),
      },
      safeCollabColumn,
    ];

    const hiredCore = [
      ...commonPrefix,
      listsColumn,
      {
        id: COLUMN_ID.Notes,
        header: translate('Notes'),
        accessorKey: 'brandNote',
        enableSorting: false,
        ...getFlexColumnSizing(),
        Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
          <Body
            size="md"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.brandNote || '-'}
          </Body>
        ),
      },
      {
        id: COLUMN_ID.Assets,
        header: translate('Assets'),
        enableSorting: false,
        ...getFlexColumnSizing(160),
        Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
          <AssetsCell creator={row.original} />
        ),
      },
    ];

    if (!hasFulfillment || !deliverablesType) {
      return [
        ...hiredCore,
        {
          id: COLUMN_ID.ShippingAddress,
          header: translate('Shipping address'),
          accessorKey: 'shippingAddressFormatted',
          enableSorting: false,
          ...getFlexColumnSizing(),
          Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
            <Body
              size="md"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textTransform: 'capitalize',
              }}
            >
              {row.original.shippingAddressFormatted || '-'}
            </Body>
          ),
        },
        {
          id: COLUMN_ID.ShippingStatus,
          header: translate('Shipping status'),
          accessorKey: 'shippingStatus',
          enableSorting: false,
          ...getFlexColumnSizing(),
          Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
            <Body size="md">{row.original.shippingStatus || '-'}</Body>
          ),
        },
        ...hiredSuffix,
      ];
    }

    const fulfillmentColumns: MRT_ColumnDef<CampaignCreatorRow>[] = [];

    if (deliverablesType === 'shipments') {
      fulfillmentColumns.push(
        {
          id: COLUMN_ID.ShippingAddress,
          header: translate('Shipping address'),
          accessorKey: 'shippingAddressFormatted',
          enableSorting: false,
          ...getFlexColumnSizing(),
          Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
            <Body
              size="md"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textTransform: 'capitalize',
              }}
            >
              {row.original.shippingAddressFormatted || '-'}
            </Body>
          ),
        },
        {
          id: COLUMN_ID.TrackingCode,
          header: translate('Tracking #'),
          ...createFulfillmentActionSortOptions('shipments'),
          ...getFlexColumnSizing(),
          Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
            <DeliverableCodeCell
              creator={row.original}
              deliverableType="shipments"
              onActionComplete={onActionComplete}
            />
          ),
        }
      );
    } else if (deliverablesType === 'gift_codes') {
      fulfillmentColumns.push({
        id: COLUMN_ID.GiftCode,
        header: translate('Gift code'),
        ...createFulfillmentActionSortOptions('gift_codes'),
        ...getFlexColumnSizing(),
        Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
          <DeliverableCodeCell
            creator={row.original}
            deliverableType="gift_codes"
            onActionComplete={onActionComplete}
          />
        ),
      });
    }

    fulfillmentColumns.push({
      id: COLUMN_ID.FulfillmentStatus,
      header: translate('Fulfillment status'),
      ...(deliverablesType
        ? createFulfillmentActionSortOptions(deliverablesType)
        : {enableSorting: false}),
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) =>
        deliverablesType ? (
          <FulfillmentStatusCell
            creator={row.original}
            deliverableType={deliverablesType}
          />
        ) : (
          <Body size="md">-</Body>
        ),
    });

    return [...hiredCore, ...fulfillmentColumns, ...hiredSuffix];
  }

  // Applications and Declined columns
  const applicationsOnlyColumns: MRT_ColumnDef<CampaignCreatorRow>[] =
    activeStatus === CREATOR_STATUS.Applications
      ? [
          {
            id: COLUMN_ID.AvgViews,
            header: translate('Avg Views'),
            accessorKey: 'averageViews',
            enableSorting: true,
            ...getFlexColumnSizing(),
            Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
              <Body size="md">
                {row.original.averageViews != null
                  ? getFollowersCountString(row.original.averageViews)
                  : '-'}
              </Body>
            ),
          },
          {
            id: COLUMN_ID.CreatorLocation,
            header: translate('Creator Location'),
            accessorKey: 'location',
            enableSorting: true,
            ...getFlexColumnSizing(),
            Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
              <TruncatedBody size="md">
                {row.original.location != null ? row.original.location : '-'}
              </TruncatedBody>
            ),
          },
          {
            id: COLUMN_ID.AudienceLocation,
            header: translate('Audience Location'),
            accessorKey: 'audienceTopCountry',
            enableSorting: false,
            ...getFlexColumnSizing(),
            Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => {
              const topCountry = row.original.audienceTopCountry;
              return (
                <TruncatedBody size="md">
                  {topCountry
                    ? `${topCountry.country} · ${topCountry.percentage}%`
                    : '-'}
                </TruncatedBody>
              );
            },
          },
        ]
      : [];

  return [
    ...commonPrefix,
    {
      id: COLUMN_ID.Followers,
      header: translate('Followers'),
      accessorKey: 'followers',
      enableSorting: true,
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
        <Body size="md">{getFollowersCountString(row.original.followers)}</Body>
      ),
    },
    {
      id: COLUMN_ID.EngRate,
      header: translate('Eng Rate'),
      accessorKey: 'engagementRate',
      enableSorting: true,
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
        <Body size="md">
          {getEngagementRateString(row.original.engagementRate)}
        </Body>
      ),
    },
    ...applicationsOnlyColumns,
    listsColumn,
    {
      id: COLUMN_ID.Offer,
      header: translate('Offer'),
      accessorKey: 'costInCents',
      enableSorting: true,
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
        <Body size="md">
          {row.original.costInCents != null
            ? formatCentsToDollars(row.original.costInCents)
            : '-'}
        </Body>
      ),
    },
    {
      id: COLUMN_ID.History,
      header: translate('History'),
      accessorKey: 'campaignHistoryCount',
      enableSorting: false,
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
        <CampaignHistoryCell
          count={row.original.campaignHistoryCount}
          rating={row.original.campaignHistoryRating}
        />
      ),
    },
    {
      id: COLUMN_ID.Applied,
      header: translate('Applied'),
      accessorKey: 'appliedDate',
      enableSorting: true,
      ...getFlexColumnSizing(),
      Cell: ({row}: {row: MRT_Row<CampaignCreatorRow>}) => (
        <Body size="md">
          {formattedDate(
            new Date(row.original.appliedDate),
            MONTH_SHORT_DAY_YEAR_FORMAT
          )}
        </Body>
      ),
    },
    safeCollabColumn,
  ];
}

export default useCreatorsTabColumns;
